<template>
  <div class="container">
    <div class="header">
      <div class="title-section">
        <h2 class="title">Items / Productos</h2>
        <p class="subtitle">Gestiona el catálogo de productos y servicios</p>
      </div>
      <button class="btn-primary" @click="nuevoItem">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Nuevo Item
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-number">{{ items.length }}</div>
        <div class="stat-label">Total Items</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ itemsActivos }}</div>
        <div class="stat-label">Activos</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">${{ precioPromedio }}</div>
        <div class="stat-label">Precio Promedio</div>
      </div>
    </div>

    <div class="search-wrapper">
      <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
      </svg>
      <input
        v-model="busqueda"
        class="search-input"
        placeholder="Buscar por nombre o código..."
        type="text"
      />
    </div>

    <div v-if="cargando" class="loading-state">Cargando items...</div>
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn-primary" @click="cargarItems">Reintentar</button>
    </div>
    <div v-else class="table-container">
      <div class="table-wrapper">
        <table class="items-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Categoría</th>
              <th class="text-right">Precio Unit.</th>
              <th>Estado</th>
              <th class="actions-header">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in itemsFiltrados" :key="item.codigo" class="table-row">
              <td class="code-cell">
                <span class="code-badge">{{ item.codigo }}</span>
              </td>
              <td class="name-cell">
                <div class="item-info">
                  <div class="item-avatar">{{ getInitials(item.nombre) }}</div>
                  <div>
                    <div class="item-name">{{ item.nombre }}</div>
                    <div class="item-sub" v-if="item.codigo_auxiliar">{{ item.codigo_auxiliar }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span :class="['tag', item.tipo === 'SERVICIO' ? 'tag-servicio' : 'tag-producto']">
                  {{ item.tipo || '—' }}
                </span>
              </td>
              <td class="category-cell">{{ item.categoria || '—' }}</td>
              <td class="text-right price-cell">${{ formatPrice(item.precio_unitario) }}</td>
              <td>
                <span :class="['status-badge', item.estado === 'ACTIVO' ? 'status-active' : 'status-inactive']">
                  <span class="status-dot"></span>
                  {{ item.estado || 'Sin estado' }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="action-buttons">
                  <button class="btn-secondary" @click="editarItem(item.codigo)" title="Editar">
                    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="m18 2 4 4-4 4-4-4 4-4z"/>
                      <path d="M10.5 12.5 2 21l-1-1 8.5-8.5"/>
                    </svg>
                  </button>
                  <button class="btn-danger" @click="eliminarItem(item.codigo, item.nombre)" title="Eliminar">
                    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="m19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="itemsFiltrados.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
        </div>
        <h3>{{ busqueda ? 'Sin resultados' : 'No hay items registrados' }}</h3>
        <p>{{ busqueda ? 'Intenta con otro término de búsqueda' : 'Comienza agregando tu primer producto al catálogo' }}</p>
        <button v-if="!busqueda" class="btn-primary" @click="nuevoItem">Agregar Primer Item</button>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '../../Servicios/ApiService'

export default {
  name: 'ListaItems',
  data () {
    return {
      items: [],
      busqueda: '',
      cargando: false,
      error: null
    }
  },
  computed: {
    itemsFiltrados () {
      if (!this.busqueda) return this.items
      const q = this.busqueda.toLowerCase()
      return this.items.filter(i =>
        (i.nombre && i.nombre.toLowerCase().includes(q)) ||
        (i.codigo && String(i.codigo).toLowerCase().includes(q)) ||
        (i.codigo_auxiliar && String(i.codigo_auxiliar).toLowerCase().includes(q))
      )
    },
    itemsActivos () {
      return this.items.filter(i => i.estado === 'ACTIVO').length
    },
    precioPromedio () {
      if (!this.items.length) return '0.00'
      const total = this.items.reduce((s, i) => s + (parseFloat(i.precio_unitario) || 0), 0)
      return (total / this.items.length).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  },
  created () {
    this.cargarItems()
  },
  methods: {
    async cargarItems () {
      this.cargando = true
      this.error = null
      try {
        const data = await ApiService.get('api/items/productos')
        this.items = Array.isArray(data) ? data : (data.items || data.data || [])
      } catch (err) {
        console.error('Error al cargar items:', err)
        this.error = 'No se pudieron cargar los items. Intenta de nuevo.'
      } finally {
        this.cargando = false
      }
    },
    nuevoItem () {
      this.$router.push('/panel/inventario/nuevo')
    },
    editarItem (codigo) {
      this.$router.push(`/panel/inventario/editar/${codigo}`)
    },
    async eliminarItem (codigo, nombre) {
      if (!confirm(`¿Eliminar el item "${nombre}"?\nEsta acción eliminará también su galería, kardex e inventario.`)) return
      try {
        await ApiService.delete(`api/items/${codigo}`)
        this.cargarItems()
      } catch (err) {
        console.error('Error al eliminar item:', err)
        alert('Error al eliminar: ' + ((err.response && err.response.data && err.response.data.mensaje) || err.message))
      }
    },
    getInitials (nombre) {
      if (!nombre) return '?'
      return nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    },
    formatPrice (valor) {
      return parseFloat(valor || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: var(--color-bg);
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding: 1.5rem 0;
}

.title-section { flex: 1; }

.title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.5rem 0;
}

.subtitle {
  color: var(--color-text-secondary);
  font-size: 1.1rem;
  margin: 0;
}

.btn-primary {
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-glow);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(19, 236, 19, 0.35);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: var(--color-surface);
  padding: 1.5rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  transition: transform 0.3s ease;
}

.stat-card:hover { transform: translateY(-4px); }

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
}

.stat-label {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
}

.search-wrapper {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border-radius: var(--radius-md);
  font-size: 1rem;
  box-sizing: border-box;
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  color: var(--color-text-primary) !important;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary) !important;
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-secondary);
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
}

.error-state p { margin-bottom: 1rem; color: var(--color-error); }

.table-container {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.table-wrapper { overflow-x: auto; }

.items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.items-table th {
  background: var(--color-surface-2);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
}

.text-right { text-align: right; }
.actions-header { text-align: center; }

.table-row {
  transition: background 0.2s;
  border-bottom: 1px solid var(--color-border);
}

.table-row:hover { background: var(--color-surface-2); }

.items-table td { padding: 1rem; vertical-align: middle; }

.code-cell { width: 120px; }

.code-badge {
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid var(--color-border);
  font-family: monospace;
}

.name-cell { min-width: 200px; }

.item-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.item-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-primary-dim);
  border: 1px solid var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.item-name {
  font-weight: 600;
  color: var(--color-text-primary);
}

.item-sub {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  margin-top: 2px;
  font-family: monospace;
}

.tag {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.tag-producto {
  background: var(--color-primary-dim);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
}

.tag-servicio {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.4);
}

.category-cell { color: var(--color-text-secondary); }

.price-cell {
  font-weight: 600;
  color: var(--color-primary);
  font-family: monospace;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active {
  background: rgba(19, 236, 19, 0.1);
  color: var(--color-primary);
}

.status-active .status-dot { background: var(--color-primary); }

.status-inactive {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

.status-inactive .status-dot { background: var(--color-error); }

.actions-cell { width: 100px; }

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.btn-secondary, .btn-danger {
  padding: 0.5rem;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-secondary {
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover {
  border-color: var(--color-primary);
  transform: translateY(-1px);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: translateY(-1px);
}

.icon { width: 16px; height: 16px; stroke-width: 2; }

.empty-state {
  padding: 4rem 2rem;
  text-align: center;
  color: var(--color-text-secondary);
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  opacity: 0.3;
}

.empty-icon svg { width: 100%; height: 100%; stroke-width: 1.5; }

.empty-state h3 {
  font-size: 1.5rem;
  color: var(--color-text-secondary);
  margin-bottom: 0.5rem;
}

.empty-state p { margin-bottom: 2rem; }

@media (max-width: 768px) {
  .container { padding: 1rem; }
  .header { flex-direction: column; align-items: flex-start; gap: 1rem; }
  .title { font-size: 2rem; }
  .stats-grid { grid-template-columns: 1fr; }
}
</style>



// WEBPACK FOOTER //
// src/components/Inventario/Lista_items.vue